import { FormSectionDto, UserProfileDto, UserRole } from "@green0meter/api-client";
import { useQueries, useQuery, UseQueryResult } from "@tanstack/react-query";

import { DOMAIN_FORMS } from "../domain";

import { formsApi } from "@api/services";
import { TANSTACK_QUERY_STALE_TIME } from "@constants";
import { roleGuard } from "@api";

export const ACTION_GET_FORM_SECTIONS = "GET_FORM_SECTIONS";

const allowedRoles = [
  UserRole.Owner,
  UserRole.Manager,
  UserRole.Respondent,
  UserRole.Viewer,
  UserRole.Auditor,
];

export const fetchFormSections = async (
  formId: string,
): Promise<FormSectionDto[]> => {
  const response = await formsApi.formsControllerGetFormSections(formId);

  return response.data;
};

export const useGetFormSections = (
  user: UserProfileDto | null,
  formId: string,
  enable: boolean = true,
): {
  formSections: FormSectionDto[] | undefined;
  formSectionsEnabled: boolean;
  formSectionsError: Error | null;
  isLoadingFormSections: boolean;
  isSuccessFormSections: boolean;
} => {
  const enabled = enable && roleGuard(user, allowedRoles);
  const queryResult = useQuery<FormSectionDto[], Error>({
    queryKey: [DOMAIN_FORMS, ACTION_GET_FORM_SECTIONS, formId],
    queryFn: () => fetchFormSections(formId),
    enabled,
    staleTime: TANSTACK_QUERY_STALE_TIME,
  });

  return {
    formSections: queryResult.data as FormSectionDto[],
    formSectionsEnabled: enabled,
    formSectionsError: queryResult.error,
    isLoadingFormSections: queryResult.isLoading || queryResult.isFetching,
    isSuccessFormSections: queryResult.isSuccess,
  };
};

export const useGetMultipleFormsSections = (
  user: UserProfileDto | null,
  formIds: string[],
  enable: boolean = true,
): {
  formsSections: UseQueryResult<FormSectionDto[], Error>[];
  formsSectionsEnabled: boolean;
  allFormsSectionsFetched: boolean;
} => {
  const enabled = enable && roleGuard(user, allowedRoles);

  const formsSections = useQueries({
    queries: formIds.map((formId) => ({
      queryKey: [DOMAIN_FORMS, ACTION_GET_FORM_SECTIONS, formId],
      queryFn: () => fetchFormSections(formId),
      enabled,
      staleTime: TANSTACK_QUERY_STALE_TIME,
    })),
  });

  const allFormsSectionsFetched = formsSections.every((query) => query.isSuccess);

  return {
    formsSections,
    formsSectionsEnabled: enabled,
    allFormsSectionsFetched,
  };
};
